import { useState, useEffect, useRef } from 'preact/hooks'
import { useForm, Controller } from 'react-hook-form'
import { MarkdownView } from '@/components/common/MarkdownView'
import {
  ChevronDown, ChevronUp, FileText, Upload, Link2, Sparkles,
  ArrowUpFromLine, Trash2, Download, Loader2, Plus, X, Building2, Pencil,
} from 'lucide-preact'
import type {
  KnowledgeBaseCategoryStatusWithConfigDto,
  KnowledgeBaseEntryViewDto,
  KnowledgeBaseEntryUpsertDto,
} from '@/types/knowledgeBase'
import { MarketingLanguage, type KnowledgeBaseLevel } from '@/types/knowledgeBase'
import type { AddressDto } from '@/types/company'
import { fieldLimits } from '@/types/field-limits.generated'
import { Select } from '@/components/common/Select'
import { TextBox } from '@/components/common/TextBox'
import { CountryDropdown } from '@/components/common/CountryDropdown'
import { FormActions } from '@/components/common/FormActions'
import { RichTextBox } from '@/components/common/RichTextBox'
import { useLookups } from '@/hooks/useLookups'
import type { EntryState } from '@/components/console/knowledgeBase/knowledgeBaseModel'
import {
  ENTRY_CHEVRON_CELL, ENTRY_COLUMNS, ENTRY_DESCRIPTION, ENTRY_NAME, ENTRY_ROW_GRID, ENTRY_STATE_CELL, EntryStateLabel, STATE_NOTE,
  WarningMark,
} from './entryStatus'
import {
  entryOwnerNote, inheritedOwner, levelMayHoldEntry, levelOwner, postalAddressLine, type LevelNames,
} from './knowledgeBaseLevels'

interface Props {
  // Narrowed by useKnowledgeBase: a category with no Config is dropped before it reaches a row.
  categoryStatus: KnowledgeBaseCategoryStatusWithConfigDto
  /** Worked out by the panel from the summary, which alone knows what emails at this level are waiting for. */
  state: EntryState
  level: KnowledgeBaseLevel
  isExpanded: boolean
  onToggle: () => void
  entry: KnowledgeBaseEntryViewDto | null
  entryLoading: boolean
  onFetchEntry: (category: string) => void
  onSave: (category: string, dto: KnowledgeBaseEntryUpsertDto) => Promise<boolean>
  onDeleteOverride: (category: string) => Promise<boolean>
  onUploadFile: (category: string, file: File) => Promise<boolean>
  onDeleteFile: (category: string, fileId: string) => Promise<boolean>
  saving: boolean
  clientCompanyId: string
  /** Whether the user may edit content at this level (workspace edit capability; always true at company level, which is admin-gated server-side) */
  userCanEdit: boolean
  /** When provided, shows an AI Populate button for this category */
  onAiPopulate?: (category: string) => void
  /** The company and workspace by name, so the row can say whose entry it shows (ORB-1027). */
  names: LevelNames
  /**
   * The company's own address, beside a workspace's own one: null when the company has none, undefined until it is read
   * or where the row does not show it.
   */
  companyAddress?: AddressDto | null
}

interface AddressFormData {
  BusinessName: string
  StreetAddress: string
  StreetAddressLineTwo: string
  City: string
  RegionOrState: string
  PostalCode: string
  CountryCode: string
}

interface CategoryFormData {
  textContent: string
  dropdownValue: string
  links: string[]
  /** The one link of a category that holds a single one (ORB-1145). */
  link: string
  address: AddressFormData
}

const EMPTY_ADDRESS: AddressFormData = {
  BusinessName: '',
  StreetAddress: '',
  StreetAddressLineTwo: '',
  City: '',
  RegionOrState: '',
  PostalCode: '',
  CountryCode: '',
}

const ADDRESS_LIMITS = fieldLimits.CompanyAddress

const ADDRESS_TEXT_FIELDS: { key: keyof typeof ADDRESS_LIMITS & keyof AddressFormData; label: string; fullWidth: boolean }[] = [
  // Printed above the address in every email footer, and required on every Company Address entry (ORB-1027).
  { key: 'BusinessName', label: 'Business name', fullWidth: true },
  { key: 'StreetAddress', label: 'Street address', fullWidth: true },
  { key: 'StreetAddressLineTwo', label: 'Address line 2', fullWidth: true },
  { key: 'City', label: 'City', fullWidth: false },
  { key: 'RegionOrState', label: 'Region or state', fullWidth: false },
  { key: 'PostalCode', label: 'Postal code', fullWidth: false },
]

function toAddressDto(address: AddressFormData): AddressDto {
  return {
    BusinessName: address.BusinessName.trim(),
    StreetAddress: address.StreetAddress.trim(),
    StreetAddressLineTwo: address.StreetAddressLineTwo.trim() || null,
    City: address.City.trim(),
    RegionOrState: address.RegionOrState.trim() || null,
    PostalCode: address.PostalCode.trim() || null,
    CountryCode: address.CountryCode,
  }
}

const MARKETING_LANGUAGE_OPTIONS = [
  { value: MarketingLanguage.USEnglish, label: 'US English' },
  { value: MarketingLanguage.UKEnglish, label: 'UK English' },
]

export function KnowledgeBaseCategoryRow({
  categoryStatus,
  state,
  level,
  isExpanded,
  onToggle,
  entry,
  entryLoading,
  onFetchEntry,
  onSave,
  onDeleteOverride,
  onUploadFile,
  onDeleteFile,
  saving,
  clientCompanyId,
  userCanEdit,
  onAiPopulate,
  names,
  companyAddress,
}: Props) {
  const { Category, CategoryDisplayName, IsPopulated, IsInherited, HasEntryAtThisLevel, Config } = categoryStatus

  const { getCountries } = useLookups()
  const [isEditing, setIsEditing] = useState(false)
  // Edit as this level's own was pressed on a closed row: the form opens once the entry has been read.
  const [editWhenRead, setEditWhenRead] = useState(false)
  const [newLink, setNewLink] = useState('')
  const [editingLinks, setEditingLinks] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const form = useForm<CategoryFormData>({
    mode: 'onChange',
    defaultValues: {
      textContent: '',
      dropdownValue: '',
      links: [],
      link: '',
      address: EMPTY_ADDRESS,
    },
  })

  const { register, handleSubmit, reset, formState: { isSubmitting } } = form

  // Reset editing state when panel closes
  useEffect(() => {
    if (!isExpanded) {
      setIsEditing(false)
      setEditWhenRead(false)
      setNewLink('')
      reset()
    }
  }, [isExpanded, reset])

  const handleToggle = () => {
    if (!isExpanded) {
      onFetchEntry(Category)
    }
    onToggle()
  }

  const startEditing = () => {
    if (entry) {
      const address = entry.Address
      reset({
        textContent: entry.TextContent || '',
        dropdownValue: entry.DropdownValue || '',
        links: entry.Links || [],
        link: entry.Links?.[0] ?? '',
        address: {
          BusinessName: address?.BusinessName || '',
          StreetAddress: address?.StreetAddress || '',
          StreetAddressLineTwo: address?.StreetAddressLineTwo || '',
          City: address?.City || '',
          RegionOrState: address?.RegionOrState || '',
          PostalCode: address?.PostalCode || '',
          CountryCode: address?.CountryCode || '',
        },
      })
      setEditingLinks([...(entry.Links || [])])
    }
    setIsEditing(true)
  }

  useEffect(() => {
    if (!editWhenRead || !isExpanded || entryLoading || entry?.Category !== Category) return
    setEditWhenRead(false)
    startEditing()
  }, [editWhenRead, isExpanded, entryLoading, entry])

  const editAsOwn = () => {
    if (isExpanded && !entryLoading && entry?.Category === Category) {
      startEditing()
      return
    }
    setEditWhenRead(true)
    if (!isExpanded) handleToggle()
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setNewLink('')
    reset()
  }

  const onSubmit = async (data: CategoryFormData) => {
    const dto: KnowledgeBaseEntryUpsertDto = {
      ClientCompanyId: clientCompanyId,
      InheritFromParent: false,
      ...(Config.SupportsText ? { TextContent: data.textContent } : {}),
      ...(Config.SupportsDropdown ? { DropdownValue: data.dropdownValue } : {}),
      ...(Config.SupportsLinks
        ? { Links: Config.SingleLink ? [data.link.trim()].filter(link => link !== '') : editingLinks }
        : {}),
      ...(Config.SupportsAddress ? { Address: toAddressDto(data.address) } : {}),
    }
    if (!(await onSave(Category, dto))) return

    setIsEditing(false)
    setNewLink('')
  }

  const handleAddLink = () => {
    const trimmed = newLink.trim()
    if (trimmed && !editingLinks.includes(trimmed)) {
      setEditingLinks([...editingLinks, trimmed])
      setNewLink('')
    }
  }

  const handleRemoveLink = (index: number) => {
    setEditingLinks(editingLinks.filter((_, i) => i !== index))
  }

  const handleFileUpload = async (e: Event) => {
    const target = e.target as HTMLInputElement
    const file = target.files?.[0]
    if (file) {
      await onUploadFile(Category, file)
      target.value = ''
    }
  }

  const countryName = (countryCode: string) =>
    getCountries().find(country => country.Key === countryCode)?.Description || countryCode

  // The level may hold its own entry of this category, and the person may change the level (ORB-1027).
  const canEdit = levelMayHoldEntry(level, Config) && userCanEdit
  const canRemoveOverride = level !== 'Company' && HasEntryAtThisLevel
  // An entry the level takes from above can be made its own straight from the row; saving leaves the other one alone.
  const canEditAsOwn = canEdit && level !== 'Company' && !HasEntryAtThisLevel && IsInherited && !isEditing
  const owner = levelOwner(level, names)
  const from = inheritedOwner(categoryStatus, names)

  const ownerNote = entryOwnerNote(categoryStatus, level, names)
  const Chevron = isExpanded ? ChevronUp : ChevronDown
  const country = entry?.Address ? countryName(entry.Address.CountryCode) : null
  const addressLine = entry?.Address && country ? postalAddressLine(entry.Address, country) : null
  const companyAddressLine = companyAddress ? postalAddressLine(companyAddress, countryName(companyAddress.CountryCode)) : null

  return (
    <div>
      {/* Edit as its own is a sibling laid over the state column, so no button sits inside the row's own. */}
      <div className={`grid ${ENTRY_COLUMNS}`}>
        <button type="button" aria-expanded={isExpanded} onClick={handleToggle} className={`group col-span-full row-start-1 ${ENTRY_ROW_GRID}`}>
          <span className="block min-w-0">
            <span className={ENTRY_NAME}>{CategoryDisplayName}</span>
            {Config.Description && <span className={ENTRY_DESCRIPTION}>{Config.Description}</span>}
          </span>
          <span className={`${ENTRY_STATE_CELL} ${canEditAsOwn ? 'pb-9' : ''}`}>
            <EntryStateLabel state={state} />
            {ownerNote && <span className={STATE_NOTE}>{ownerNote}</span>}
          </span>
          <Chevron aria-hidden="true" className={`${ENTRY_CHEVRON_CELL} text-slate-400 dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300`} />
        </button>
        {canEditAsOwn && (
          <button type="button" onClick={editAsOwn} disabled={saving} aria-label={`Edit as ${owner}'s own: ${CategoryDisplayName}`}
            className="relative z-[1] row-start-1 col-start-1 sm:col-start-2 self-end justify-self-start mb-3 -ml-1.5 inline-flex items-center gap-1 px-1.5 py-1 rounded-md text-xs font-medium whitespace-nowrap text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed">
            <Pencil className="w-3 h-3 flex-none" aria-hidden="true" />
            Edit as {owner}'s own
          </button>
        )}
      </div>

      {/* Sunken under its row, edge to edge: an expanded row is not a second card. */}
      {isExpanded && (
        <div className="-mx-6 px-6 py-5 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
          {entryLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Inheritance banner */}
              {entry?.IsInherited && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                  <ArrowUpFromLine className="w-4 h-4 flex-shrink-0" />
                  <span>
                    This is <strong>{from}</strong>'s.
                    {canEdit ? ` Saving an edit here makes it ${owner}'s own; ${from}'s stays as it is.` : ''}
                  </span>
                </div>
              )}

              {/* Read-only view or Edit form */}
              {!isEditing ? (
                <div className="space-y-3">
                  {/* Text content — rendered as markdown */}
                  {Config.SupportsText && entry?.TextContent && (
                    <MarkdownView className="prose-slate text-slate-700 dark:text-slate-300">{entry.TextContent}</MarkdownView>
                  )}

                  {/* Dropdown value */}
                  {Config.SupportsDropdown && entry?.DropdownValue && (
                    <div className="text-sm text-slate-700 dark:text-slate-300">
                      <span className="font-medium">Language: </span>
                      {entry.DropdownValue === MarketingLanguage.USEnglish ? 'US English' : 'UK English'}
                    </div>
                  )}

                  {/* Company address: the business name and address every email footer prints (ORB-1027) */}
                  {Config.SupportsAddress && entry?.Address && (
                    <div className="flex flex-col gap-5">
                      {level === 'Workspace' && HasEntryAtThisLevel && (
                        <p className="flex items-start gap-2 max-w-[80ch] text-sm text-slate-600 dark:text-slate-300">
                          <Building2 className="w-4 h-4 flex-none mt-0.5 text-slate-500 dark:text-slate-400" aria-hidden="true" />
                          <span>
                            <span className="font-medium text-slate-900 dark:text-white">{owner}'s own address.</span>
                            {companyAddress && companyAddressLine
                              ? ` A workspace without its own uses ${names.company ?? 'the company'}'s: ${[companyAddress.BusinessName, companyAddressLine].filter(Boolean).join(', ')}.`
                              : ''}
                          </span>
                        </p>
                      )}
                      <dl className="grid grid-cols-1 sm:grid-cols-[148px_minmax(0,1fr)] gap-x-6 gap-y-1 sm:gap-y-3.5 text-sm">
                        <dt className="font-medium text-slate-500 dark:text-slate-400">Business name</dt>
                        <dd className="mb-2 sm:mb-0 font-medium text-slate-900 dark:text-white">
                          {entry.Address.BusinessName || (
                            <span className="inline-flex items-center gap-1.5 font-normal text-amber-800 dark:text-amber-300">
                              <WarningMark className="w-4 h-4 flex-none" />Not set. Emails need one.
                            </span>
                          )}
                        </dd>
                        <dt className="font-medium text-slate-500 dark:text-slate-400">Postal address</dt>
                        <dd className="mb-2 sm:mb-0">
                          <address className="not-italic text-slate-900 dark:text-white">
                            <span className="block">{entry.Address.StreetAddress}</span>
                            {entry.Address.StreetAddressLineTwo && <span className="block">{entry.Address.StreetAddressLineTwo}</span>}
                            <span className="block">{[entry.Address.City, entry.Address.RegionOrState].filter(Boolean).join(', ')}</span>
                            {entry.Address.PostalCode && <span className="block">{entry.Address.PostalCode}</span>}
                            <span className="block">{country}</span>
                          </address>
                        </dd>
                        {entry.Address.BusinessName && addressLine && (
                          <>
                            <dt className="font-medium text-slate-500 dark:text-slate-400">In email footers</dt>
                            <dd className="text-slate-600 dark:text-slate-300">{`${entry.Address.BusinessName}, ${addressLine}`}</dd>
                          </>
                        )}
                      </dl>
                    </div>
                  )}

                  {/* Links */}
                  {Config.SupportsLinks && entry?.Links && entry.Links.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{Config.SingleLink ? 'URL' : 'Links'}</span>
                      {entry.Links.map((link, i) => (
                        <a
                          key={i}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          <Link2 className="w-3 h-3" />
                          {link}
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Files */}
                  {Config.SupportsUpload && entry?.Files && entry.Files.length > 0 && (
                    <div className="space-y-2">
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Files</span>
                      {entry.Files.map((file) => (
                        <div key={file.FileId} className="flex items-center justify-between gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="text-sm text-slate-700 dark:text-slate-300 truncate">{file.FileName}</span>
                            <span className="text-xs text-slate-400 flex-shrink-0">
                              {(file.FileSizeBytes / 1024 / 1024).toFixed(1)} MB
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <a
                              href={file.DownloadUrl ?? undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                            {canEdit && !entry.IsInherited && (
                              <button
                                type="button"
                                onClick={() => onDeleteFile(Category, file.FileId)}
                                className="p-1 text-slate-400 hover:text-red-500"
                                title="Delete file"
                                disabled={saving}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Empty state */}
                  {!IsPopulated && !entry?.TextContent && !entry?.DropdownValue && (
                    <p className="text-sm text-slate-400 dark:text-slate-500 italic">No content yet</p>
                  )}

                  {/* Action buttons */}
                  {canEdit && (
                    <div className="flex items-center gap-2 pt-2">
                      <button
                        type="button"
                        onClick={startEditing}
                        className="text-sm px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                      >
                        {IsPopulated ? 'Edit' : 'Add Content'}
                      </button>

                      {Config.SupportsUpload && !entry?.IsInherited && (
                        <>
                          <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            onChange={handleFileUpload}
                          />
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            disabled={saving}
                          >
                            <Upload className="w-3.5 h-3.5" />
                            Upload File
                          </button>
                        </>
                      )}

                      {onAiPopulate && (
                        <button
                          type="button"
                          onClick={() => onAiPopulate(Category)}
                          className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                          disabled={saving}
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          AI Populate
                        </button>
                      )}

                      {canRemoveOverride && (
                        <button
                          type="button"
                          onClick={() => onDeleteOverride(Category)}
                          className="text-sm px-3 py-1.5 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-auto"
                          disabled={saving}
                        >
                          {level === 'Campaign' ? 'Revert to Workspace' : `Revert to ${names.company ?? 'the company'}'s`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Edit mode — uses react-hook-form + common components */
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  {entry?.IsInherited && (
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      Saving makes this {owner}'s own. {from}'s stays as it is.
                    </p>
                  )}
                  {Config.SupportsText && (
                    <Controller
                      name="textContent"
                      control={form.control}
                      render={({ field }) => (
                        <RichTextBox
                          name="textContent"
                          placeholder={`Enter ${CategoryDisplayName.toLowerCase()} content...`}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      )}
                    />
                  )}

                  {Config.SupportsDropdown && (
                    <Select
                      label="Marketing Language"
                      options={MARKETING_LANGUAGE_OPTIONS}
                      placeholder="Select language..."
                      {...register('dropdownValue')}
                    />
                  )}

                  {Config.SupportsAddress && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {ADDRESS_TEXT_FIELDS.map(({ key, label, fullWidth }) => (
                        <div key={key} className={fullWidth ? 'sm:col-span-2' : undefined}>
                          <Controller
                            name={`address.${key}`}
                            control={form.control}
                            rules={{
                              required: ADDRESS_LIMITS[key].IsRequired ? `${label} is required` : false,
                              maxLength: { value: ADDRESS_LIMITS[key].MaxLength, message: `${label} must be ${ADDRESS_LIMITS[key].MaxLength} characters or fewer` },
                            }}
                            render={({ field, fieldState }) => (
                              <TextBox
                                name={`address.${key}`}
                                label={label}
                                required={ADDRESS_LIMITS[key].IsRequired}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                maxLength={ADDRESS_LIMITS[key].MaxLength}
                                error={fieldState.error?.message}
                              />
                            )}
                          />
                        </div>
                      ))}
                      <Controller
                        name="address.CountryCode"
                        control={form.control}
                        rules={{ required: 'Country is required' }}
                        render={({ field, fieldState }) => (
                          <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                              Country <span className="text-red-500">*</span>
                            </label>
                            <CountryDropdown value={field.value} onChange={field.onChange} error={fieldState.error?.message} />
                          </div>
                        )}
                      />
                    </div>
                  )}

                  {Config.SupportsLinks && Config.SingleLink && (
                    <Controller
                      name="link"
                      control={form.control}
                      render={({ field }) => (
                        <TextBox
                          name="link"
                          label="URL"
                          type="url"
                          placeholder="https://"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      )}
                    />
                  )}

                  {Config.SupportsLinks && !Config.SingleLink && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Links</label>
                      <div className="space-y-2">
                        {editingLinks.map((link, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="flex-1 text-sm text-slate-700 dark:text-slate-300 truncate">{link}</span>
                            <button type="button" onClick={() => handleRemoveLink(i)} className="text-red-500 hover:text-red-700">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <div className="flex items-center gap-2">
                          <input
                            type="url"
                            value={newLink}
                            onInput={(e) => setNewLink((e.target as HTMLInputElement).value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddLink() } }}
                            placeholder="https://..."
                            className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <button type="button" onClick={handleAddLink} className="p-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600">
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <FormActions
                    isSubmitting={saving || isSubmitting}
                    isSaveDisabled={saving || isSubmitting}
                    onCancel={cancelEditing}
                    saveText={saving ? 'Saving...' : 'Save'}
                    className="px-0 bg-transparent border-0"
                  />
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
